/* eslint-disable max-lines-per-function */
import { commonTranslationKeys } from '@dreryk/react-common';
import {
   ActionEventInterceptor,
   DownloadIcon,
   IconSize,
   ModalDropdown,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';
import { MedicalDocumentationPasswordDetailsShowModalInterceptor } from '~libs.desktop.shared.medical-documentation';

import { medicalDocumentationCreateTranslationKeys } from '../../../translations';
import { MedicalDocumentationCreateFormController } from '../MedicalDocumentationCreateForm.types';

import { useGenerateDocumentationCommand } from './useGenerateDocumentationCommand';

type MedicalDocumentationDownloadActionProps = Pick<
   MedicalDocumentationCreateFormController,
   'handleSubmit' | 'trigger'
> &
   Readonly<{
      patientId: number;
      onCreate: VoidFunction;
   }>;

export const MedicalDocumentationDownloadAction = (props: MedicalDocumentationDownloadActionProps): JSX.Element => {
   const {
      openAccountAuthorizationModal: openEncryptedMedicalDocumentationAccountAuthorizationModal,
      renderAccountAuthorizationnModal: renderEncryptedMedicalDocumentationAccountAuthorizationModal,
   } = useAccountAuthorizationModal();

   const {
      openAccountAuthorizationModal: openUnencryptedMedicalDocumentationAccountAuthorizationModal,
      renderAccountAuthorizationnModal: renderUnencryptedMedicalDocumentationAccountAuthorizationModal,
   } = useAccountAuthorizationModal();

   const { executeAsync: generateUnencryptedDocumentation } = useGenerateDocumentationCommand({
      patientId: props.patientId,
      onAuthenticationRequired: openUnencryptedMedicalDocumentationAccountAuthorizationModal,
      encrypted: false,
   });

   const {
      executeAsync: generateEncrytpedDocumentation,
      data: medicalDocumentationEncryptedGenerationResult,
   } = useGenerateDocumentationCommand({
      patientId: props.patientId,
      onAuthenticationRequired: openEncryptedMedicalDocumentationAccountAuthorizationModal,
      encrypted: true,
   });

   return (
      <>
         <ModalDropdown
            testId={'medicalDocumentationDropdownButton'}
            triggerLabel={commonTranslationKeys.download}
            triggerIcon={<DownloadIcon testId={'downloadIcon'} size={IconSize.SMALL} />}
         >
            <ActionEventInterceptor
               action={props.handleSubmit(generateUnencryptedDocumentation)}
               blockingOperation={medicalDocumentationCreateTranslationKeys.generatingDocumentation}
            >
               <ValidateFormEventInterceptor trigger={props.trigger} silent>
                  <ModalDropdown.Item testId={'downloadUnencrypted'} onClick={props.onCreate}>
                     {medicalDocumentationCreateTranslationKeys.createPatientRecordsModal.actions.downloadUnencrypted}
                  </ModalDropdown.Item>
               </ValidateFormEventInterceptor>
            </ActionEventInterceptor>
            <ActionEventInterceptor
               action={props.handleSubmit(generateEncrytpedDocumentation)}
               blockingOperation={medicalDocumentationCreateTranslationKeys.generatingDocumentation}
            >
               <ValidateFormEventInterceptor trigger={props.trigger} silent>
                  <MedicalDocumentationPasswordDetailsShowModalInterceptor
                     filePassword={medicalDocumentationEncryptedGenerationResult?.encryptionPassword ?? null}
                     title={medicalDocumentationCreateTranslationKeys.createdAndSentPatientRecords}
                  >
                     <ModalDropdown.Item testId={'downloadEncrypted'} onClick={props.onCreate}>
                        {medicalDocumentationCreateTranslationKeys.createPatientRecordsModal.actions.downloadEncrypted}
                     </ModalDropdown.Item>
                  </MedicalDocumentationPasswordDetailsShowModalInterceptor>
               </ValidateFormEventInterceptor>
            </ActionEventInterceptor>
         </ModalDropdown>
         {renderUnencryptedMedicalDocumentationAccountAuthorizationModal({
            onAuthorization: props.handleSubmit(generateUnencryptedDocumentation),
         })}
         {renderEncryptedMedicalDocumentationAccountAuthorizationModal({
            onAuthorization: props.handleSubmit(generateEncrytpedDocumentation),
         })}
      </>
   );
};
